import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Dices, Link as LinkIcon, RefreshCw } from "lucide-react";

import api from "../../../const/axios";
import Question from "../Package/Single/Question";
import { DiscussionProvider } from "../Package/DiscussionContext";
import ToggleAnswersButton from "../../Elements/ToggleAnswersButton";
import RandomPackageCountPicker, {
  RANDOM_PACKAGE_DEFAULT,
  clampRandomPackageCount,
} from "./RandomPackageCountPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface RandomQuestion {
  values: { id: number; text: string; answer: string; rekvizit: { text: boolean; rekvizit: string } | null }[];
  isRekvizitForAll?: boolean;
  notebefore?: string;
  considered?: string;
  comment?: string;
  sources: string[];
  authors: { fullname: string }[];
  package: { id: number; name: string; game?: string } | null;
}

type Status = "loading" | "ready" | "empty" | "error";

const RandomPackage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [questions, setQuestions] = useState<RandomQuestion[]>([]);
  const [status, setStatus] = useState<Status>("loading");
  const [opens, setOpens] = useState<number[]>([]);
  const [openCommentQuestionId, setOpenCommentQuestionId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const idsParam = searchParams.get("ids");
  const countParam = searchParams.get("count");
  const requestedCount = clampRandomPackageCount(Number(countParam) || RANDOM_PACKAGE_DEFAULT);

  // A fresh `?count=N` request is generated once and then pinned to `?ids=...`,
  // so reloading or sharing the URL shows the exact same package.
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setOpens([]);
    setOpenCommentQuestionId(null);
    const params = idsParam ? { ids: idsParam } : { count: requestedCount };
    api.get("questions/random-package", { params })
      .then((res) => {
        if (cancelled) return;
        const list: RandomQuestion[] = res.data?.questions ?? [];
        setQuestions(list);
        setStatus(list.length ? "ready" : "empty");
        if (!idsParam && list.length) {
          setSearchParams({ ids: list.map((q) => q.values[0].id).join(",") }, { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setQuestions([]);
        setStatus(err?.response?.status === 404 ? "empty" : "error");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsParam, countParam]);

  const questionIds = useMemo(() => questions.map((q) => q.values[0].id), [questions]);
  const allOpen = questionIds.length > 0 && questionIds.every((id) => opens.includes(id));

  const toggleOpen = useCallback((id: number) => {
    setOpens((prev) => (prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]));
  }, []);

  const regenerate = (count: number) => {
    setPickerOpen(false);
    setSearchParams({ count: String(count) });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>Təsadüfi paket | 3sual.az</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        {/* Header */}
        <div className="rounded-2xl border border-violet-100 dark:border-violet-900 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 dark:from-violet-950 dark:via-fuchsia-950 dark:to-pink-950 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="rounded-xl bg-violet-100 dark:bg-violet-900 p-2.5 text-violet-600 dark:text-violet-300">
                <Dices className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500 dark:text-violet-300">Nə? Harada? Nə zaman?</p>
                <h1 className="text-left tracking-normal text-xl sm:text-2xl font-black text-gray-900 dark:text-gray-50 leading-tight">Təsadüfi paket</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {status === "ready"
                    ? `${questions.length} sual müxtəlif paketlərdən təsadüfi seçilib.`
                    : "Müxtəlif paketlərdən təsadüfi seçilmiş suallar."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap sm:shrink-0 items-center gap-2">
              {status === "ready" && (
                <ToggleAnswersButton isOpen={allOpen} onToggle={() => setOpens(allOpen ? [] : questionIds)} className="self-auto h-9 py-0" />
              )}
              <Button variant="outline" size="sm" onClick={copyLink} disabled={status !== "ready"} className="gap-1.5">
                <LinkIcon className="h-4 w-4" />
                {copied ? "Kopyalandı!" : "Paylaş"}
              </Button>
              <Button size="sm" onClick={() => setPickerOpen(true)} className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white">
                <RefreshCw className="h-4 w-4" />
                Yeni paket
              </Button>
            </div>
          </div>
        </div>

        {/* Body */}
        {status === "ready" ? (
          <DiscussionProvider entityType="question" ids={questionIds}>
            <div className="space-y-3">
              {questions.map((q, index) => (
                <div
                  key={q.values[0].id}
                  className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-5 py-3.5 [&>div:last-child]:pt-2.5 [&>div:last-child]:pb-0"
                >
                  {/* Source package, same treatment as the author questions page */}
                  {q.package && (
                    <div className="pb-2 border-b border-gray-100 dark:border-gray-800">
                      <Link
                        to={`/packages/${q.package.id}`}
                        className="text-sm font-semibold text-blue-500 hover:underline dark:text-indigo-300"
                      >
                        {q.package.name}{q.package.game ? ` — ${q.package.game}` : ""}
                      </Link>
                    </div>
                  )}
                  <Question
                    question={q}
                    index={index}
                    opens={opens}
                    toggleOpen={toggleOpen}
                    commentsOpen={openCommentQuestionId === q.values[0].id}
                    onToggleComments={() =>
                      setOpenCommentQuestionId((prev) => (prev === q.values[0].id ? null : q.values[0].id))
                    }
                  />
                </div>
              ))}
            </div>
          </DiscussionProvider>
        ) : (
          <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
            {status === "loading" && (
              <div className="animate-pulse space-y-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-1/4" />
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-full" />
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-2/3" />
                  </div>
                ))}
              </div>
            )}

            {(status === "empty" || status === "error") && (
              <div className="py-10 text-center space-y-3">
                <p className="text-gray-500 dark:text-gray-400">
                  {status === "empty" ? "Uyğun sual tapılmadı." : "Paketi yükləmək mümkün olmadı."}
                </p>
                <Button variant="outline" onClick={() => regenerate(requestedCount)}>Yenidən cəhd et</Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Yeni təsadüfi paket</DialogTitle>
          </DialogHeader>
          <RandomPackageCountPicker
            initial={status === "ready" ? questions.length : requestedCount}
            onSubmit={regenerate}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RandomPackage;
